import React from 'react';
import { Award, Calendar, MessageSquare, TrendingUp, Settings, MapPin, Users, ChevronRight, Trash2, AlertTriangle, X, BarChart3 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useActivities } from '../context/ActivitiesContext';
import { canManagePlatform } from '../utils/permissions';
import { getRoomPath, getDiscussionPath } from '../utils';
import { getInterestLabel } from '../utils/interests';
import { api } from '../api';

const DeleteConfirmationModal = ({ activity, onConfirm, onCancel, isDeleting }) => {
  if (!activity) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-full -mr-16 -mt-16 opacity-50"></div>
        
        <div className="relative z-10">
          <div className="w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6">
            <AlertTriangle size={32} />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Delete {activity.actType}?</h2>
          <p className="text-gray-500 font-medium mb-8 leading-relaxed">
            Are you sure you want to delete <span className="text-gray-900 font-bold italic">"{activity.title}"</span>? 
            This action cannot be undone and will hide it from everyone.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 bg-red-600 text-white font-bold py-4 rounded-2xl shadow-lg shadow-red-100 hover:bg-red-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isDeleting ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button 
              onClick={onCancel}
              disabled={isDeleting}
              className="flex-1 bg-gray-50 text-gray-500 font-bold py-4 rounded-2xl hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
        
        <button 
          onClick={onCancel}
          className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-900 transition-colors z-20"
        >
          <X size={20} />
        </button>
      </motion.div>
    </motion.div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white p-6 rounded-3xl border border-gray-100 flex flex-col items-center md:items-start text-center md:text-left">
    <div className="flex items-center justify-between md:justify-between w-full mb-4">
      <div className={`p-3 rounded-2xl ${color} text-white`}>
        <Icon size={20} />
      </div>
      <span className="text-2xl font-bold text-gray-900">{value}</span>
    </div>
    <p className="text-sm text-gray-500 font-bold uppercase tracking-[0.15em]">{label}</p>
  </div>
);

const Profile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { rooms, discussions, communities, setRooms, setDiscussions } = useActivities();
  const isPlatformAdmin = canManagePlatform(user);

  const [extraActivities, setExtraActivities] = React.useState([]);
  const [loadingExtra, setLoadingExtra] = React.useState(false);
  const [itemToDelete, setItemToDelete] = React.useState(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!user) return;
    
    const fetchExtra = async () => {
      setLoadingExtra(true);
      try {
        const [qRes, iRes, pRes] = await Promise.all([
          api.get(`/questions?author_id=${user.id}`),
          api.get(`/ideas?author_id=${user.id}`),
          api.get(`/polls?author_id=${user.id}`)
        ]);

        const getItems = (res) => Array.isArray(res) ? res : (res?.data || []);
        
        const qItems = getItems(qRes).map(i => ({ ...i, actType: 'question', title: i.text || 'Question' }));
        const iItems = getItems(iRes).map(i => ({ ...i, actType: 'idea', title: i.text || 'Idea' }));
        const pItems = getItems(pRes).map(i => ({ ...i, actType: 'poll', title: i.question || 'Poll' }));

        setExtraActivities([...qItems, ...iItems, ...pItems]);
      } catch (err) {
        console.error('Failed to fetch extra activities', err);
      } finally {
        setLoadingExtra(false);
      }
    };

    fetchExtra();
  }, [user]);

  const handleDelete = (e, activity) => {
    e.preventDefault();
    e.stopPropagation();
    setItemToDelete(activity);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      let endpoint = '';
      switch(itemToDelete.actType) {
        case 'room': endpoint = `/rooms/${itemToDelete.id}`; break;
        case 'discussion': endpoint = `/discussions/${itemToDelete.id}`; break;
        case 'question': endpoint = `/questions/${itemToDelete.id}`; break;
        case 'idea': endpoint = `/ideas/${itemToDelete.id}`; break;
        case 'poll': endpoint = `/polls/${itemToDelete.id}`; break;
        default: return;
      }
      
      await api.delete(endpoint);
      
      if (itemToDelete.actType === 'room') {
        setRooms(prev => prev.filter(r => r.id !== itemToDelete.id));
      } else if (itemToDelete.actType === 'discussion') {
        setDiscussions(prev => prev.filter(d => d.id !== itemToDelete.id));
      } else {
        setExtraActivities(prev => prev.filter(a => !(a.id === itemToDelete.id && a.actType === itemToDelete.actType)));
      }
      setItemToDelete(null);
    } catch (err) {
      console.error('Failed to delete activity', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const userActivity = React.useMemo(() => {
    if (!user) return [];
    const validRooms = Array.isArray(rooms) ? rooms : [];
    const validDiscussions = Array.isArray(discussions) ? discussions : [];
    
    const combined = [
      ...validRooms.filter(r => String(r.owner_id) === String(user.id)).map(r => ({ ...r, actType: 'room' })),
      ...validDiscussions.filter(d => String(d.author_id) === String(user.id)).map(d => ({ ...d, actType: 'discussion' })),
      ...extraActivities
    ];

    return combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10);
  }, [user, rooms, discussions, extraActivities]);

  const reputation = React.useMemo(() => {
    const achievements = Array.isArray(user?.achievements) ? user.achievements : [];
    return achievements.reduce((sum, a) => sum + (a.points || 0), 0) || 0;
  }, [user.achievements]);

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
      {/* Header */}
      <div className="bg-white rounded-3xl border border-gray-100 p-6 md:p-8 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-indigo/10 rounded-full -mr-32 -mt-32 opacity-50"></div>
        
        <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
          <div className="w-28 h-28 rounded-3xl bg-brand-indigo border-4 border-white shadow-2xl overflow-hidden">
            <img src={user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.name}`} alt="avatar" className="w-full h-full object-cover" />
          </div>
          
          <div className="text-center md:text-left flex-1">
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
              <h1 className="text-3xl font-bold text-gray-900">{user.name}</h1>
              <span className="bg-brand-indigo text-white text-xxs font-bold px-3 py-1 rounded-full uppercase tracking-[0.15em] self-center">
                {isPlatformAdmin ? 'Platform Admin' : 'Community Member'}
              </span>
            </div>
            <p className="text-gray-500 font-medium mb-4 max-w-lg leading-relaxed text-sm">
              Member of the Chooziq community. {Array.isArray(user.interests) && user.interests.length > 0 ? `Interests: ${user.interests.map(id => getInterestLabel(id)).join(', ')}` : 'Passionate about creating realtime collaborative experiences.'}
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-4 text-xs font-bold text-gray-400">
              <div className="flex items-center gap-1.5">
                <MapPin size={14} />
                {user.city && user.country ? `${user.city}, ${user.country}` : user.country || user.city || 'Global'}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar size={14} />
                Joined {user.joined || 'May 2026'}
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => navigate('/settings')}
            className="p-3 bg-gray-50 text-gray-600 rounded-2xl hover:bg-gray-100 transition-all self-start hidden md:block"
          >
            <Settings size={20} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Sessions" value={user.rooms_count || 0} icon={Calendar} color="bg-brand-indigo" />
        <StatCard label="Communities" value={user.communities_count || 0} icon={Users} color="bg-purple-600" />
        <StatCard label="Contributions" value={user.discussions_count || 0} icon={MessageSquare} color="bg-orange-600" />
        <StatCard label="Reputation" value={reputation} icon={TrendingUp} color="bg-green-600" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Level Progression */}
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900 tracking-tight">Status</h2>
            <span className="text-sm font-bold text-brand-indigo uppercase tracking-[0.15em]">
              {user.rooms_count > 5 ? 'Power User' : 'New Member'}
            </span>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">
              <span>Platform Standing</span>
              <span>Active</span>
            </div>
            <div className="h-3 w-full bg-gray-50 rounded-full overflow-hidden p-1">
              <div className="h-full w-[100%] bg-green-500 rounded-full shadow-lg shadow-green-100"></div>
            </div>
            <p className="text-sm text-gray-500 font-medium">You are in <span className="text-gray-900 font-bold">good standing</span>. Continue contributing to earn badges.</p>
          </div>
        </div>

        {/* Top Badges */}
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col items-center md:items-start text-center md:text-left">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight mb-4">Badges</h2>
          <div className="flex flex-wrap justify-center md:justify-start gap-3">
            {isPlatformAdmin && <div className="px-3 py-1.5 rounded-xl font-bold text-xxs uppercase tracking-[0.15em] bg-purple-100 text-purple-600">Platform Admin</div>}
            {Array.isArray(user.achievements) && user.achievements.length > 0 ? (
              user.achievements.map((achievement) => (
                <div 
                  key={achievement.id} 
                  title={achievement.description}
                  className="px-3 py-1.5 rounded-xl font-bold text-xxs uppercase tracking-[0.15em] bg-yellow-100 text-yellow-700 flex items-center gap-1.5 shadow-sm"
                >
                  <Award size={12} />
                  {achievement.name}
                </div>
              ))
            ) : (
              <div className="px-3 py-1.5 rounded-xl font-bold text-xxs uppercase tracking-[0.15em] bg-gray-100 text-gray-400">No badges yet</div>
            )}
            <div className="px-3 py-1.5 rounded-xl font-bold text-xxs uppercase tracking-[0.15em] bg-brand-indigo/10 text-brand-indigo">Early Adopter</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <h2 className="text-xl font-bold text-gray-900 mb-4 text-center md:text-left">Recent Contributions</h2>
      <div className="space-y-3">
        {Array.isArray(userActivity) && userActivity.length > 0 ? (
          userActivity.map((activity, i) => {
            const community = (Array.isArray(communities) ? communities : []).find(c => String(c.id) === String(activity.community_id));
            
            let path = '#';
            if (activity.actType === 'room') path = getRoomPath(activity);
            else if (activity.actType === 'discussion') path = getDiscussionPath(activity);
            else if (activity.room_id) path = `/rooms/${activity.room_id}`;
            else if (activity.community_id) path = `/communities/${activity.community_id}`;

            const getTypeLabel = (type) => {
              switch(type) {
                case 'room': return 'Session';
                case 'discussion': return 'Discussion';
                case 'question': return 'Question';
                case 'idea': return 'Idea';
                case 'poll': return 'Poll';
                default: return 'Activity';
              }
            };
            
            return (
              <Link to={path} key={`${activity.actType}-${activity.id}`} className="bg-white p-5 rounded-2xl border border-gray-100 flex items-center justify-between hover:border-brand-indigo/20 hover:shadow-xl hover:shadow-brand-indigo/5 transition-all active:scale-[0.98] cursor-pointer group">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-brand-indigo/10 group-hover:text-brand-indigo transition-colors">
                    {activity.actType === 'room' ? <Calendar size={18} /> : 
                     activity.actType === 'poll' ? <BarChart3 size={18} /> :
                     activity.actType === 'idea' ? <TrendingUp size={18} /> :
                     <MessageSquare size={18} />}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm group-hover:text-brand-indigo transition-colors line-clamp-1">{activity.title}</h3>
                     <p className="text-xs text-gray-500 font-medium">
                       {community?.name || 'General'} • {new Date(activity.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xxs font-bold text-brand-indigo bg-brand-indigo/10 px-2 py-1 rounded-full uppercase tracking-[0.15em]">
                    {getTypeLabel(activity.actType)}
                  </span>
                  <button 
                    onClick={(e) => handleDelete(e, activity)}
                    className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                  <ChevronRight size={16} className="text-gray-200 group-hover:text-brand-indigo transition-all group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })
        ) : (
          <div className="bg-white p-8 rounded-2xl border border-dashed border-gray-200 text-center">
            {loadingExtra ? (
              <p className="text-gray-500 font-medium">Loading contributions...</p>
            ) : (
              <p className="text-gray-500 font-medium">No recent contributions found.</p>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {itemToDelete && (
          <DeleteConfirmationModal 
            activity={itemToDelete}
            onConfirm={confirmDelete}
            onCancel={() => setItemToDelete(null)}
            isDeleting={isDeleting}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Profile;
